import { useState } from 'react';

import { Navigate, useNavigate, useRouter } from '@tanstack/react-router';

import { Download, LogOut, Monitor, Trash2, UserCog } from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { useActiveOrg, useAuthentication, usePermissions } from '@src/context/hooks';
import {
  useDeleteOrgMutation,
  useMineQuery,
  useOrgExportMutation,
  useQuery,
  useSignOutEverywhereMutation,
  useTeamQuery,
  useTransferOwnershipMutation,
} from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { ConfirmDialog, firstApiError } from './controls';
import PageHeader from './PageHeader';
import { DeleteOrgModal, TransferModal } from './SecurityModals';


/** Section card wrapper — title/subtitle on the left, an action slot on the
 * right (or below on mobile), matching the .pen Security cards. */
function Card({ title, subtitle, danger, children }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-[10px] border bg-surface-1 p-4 lg:flex-row lg:items-center',
        danger ? 'border-danger/40' : 'border-hairline',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-sm font-semibold', danger ? 'text-danger' : 'text-ink')}>{title}</span>
        <span className="text-xs text-ink-subtle">{subtitle}</span>
      </span>

      {children}
    </div>
  );
}

Card.displayName = 'Card';


/** Best-effort browser/OS label from a UA string — the design only needs a
 * short, human hint, not exhaustive parsing. */
function describeAgent(userAgent) {
  if (!userAgent) {
    return 'Unknown device';
  }
  const browser = (/Edg/i).test(userAgent)
    ? 'Edge'
    : (/OPR|Opera/i).test(userAgent)
      ? 'Opera'
      : (/Chrome/i).test(userAgent)
        ? 'Chrome'
        : (/Firefox/i).test(userAgent)
          ? 'Firefox'
          : (/Safari/i).test(userAgent)
            ? 'Safari'
            : 'Browser';
  const os = (/Windows/i).test(userAgent)
    ? 'Windows'
    : (/Mac OS|Macintosh/i).test(userAgent)
      ? 'macOS'
      : (/iPhone|iPad|iOS/i).test(userAgent)
        ? 'iOS'
        : (/Android/i).test(userAgent)
          ? 'Android'
          : (/Linux/i).test(userAgent)
            ? 'Linux'
            : '';
  return os ? `${browser} · ${os}` : browser;
}


function SessionsCard({ sessions, loading, error }) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="px-1 text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
        Active sessions
      </span>

      {loading
        ? (
          <span
            className={cn(
              'rounded-[9px] border border-hairline bg-surface-1 px-4 py-5 text-center text-[13px] text-ink-subtle',
            )}
          >
            Loading sessions…
          </span>
        )
        : error
          ? (
            <span
              className={cn(
                'rounded-[9px] border border-hairline bg-surface-1 px-4 py-5 text-center text-[13px] text-danger',
              )}
            >
              Could not load sessions.
            </span>
          )
          : sessions.map((session, index) => (
            <div
              key={`${session.user_agent || 'session'}-${index}`}
              className="flex items-center gap-3 rounded-[9px] border border-hairline bg-surface-1 px-3.5 py-3"
            >
              <span className="grid size-[34px] shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-muted">
                <Monitor className="size-[17px]" />
              </span>

              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13.5px] font-semibold text-ink">
                  {describeAgent(session.user_agent)}
                </span>

                <span className="truncate text-[11.5px] text-ink-subtle">{session.last_seen || ''}</span>
              </span>

              {session.current
                ? (
                  <span className="rounded-full bg-success/15 px-2.5 py-[3px] text-[10.5px] font-semibold text-success">
                    This device
                  </span>
                )
                : null}
            </div>
          ))}
    </div>
  );
}

SessionsCard.displayName = 'SessionsCard';


/** Security & danger zone (#166, .pen V9L8j), Owner only. Active sessions +
 * sign-out-everywhere (kills all tokens incl. this one → local logout +
 * redirect), transfer ownership (→ caller becomes Manager), delete org
 * (→ drop selection, re-fetch /sellers/mine, route to onboarding or first org),
 * and a data-export zip download. Other roles are redirected to the hub. */
function Security() {
  const { toasts, push } = useToasts();
  const router = useRouter();
  const navigate = useNavigate();
  const auth = useAuthentication();
  // Mine query retained only for its cache key (transfer / delete invalidate it).
  const { queryKey: mineKey } = useMineQuery();
  const { seller } = useActiveOrg();
  const { canDanger: isOwner } = usePermissions();

  const sessionsQuery = useQuery('/users/me/sessions/', { enabled: Boolean(seller) && isOwner });
  const teamQuery = useTeamQuery(seller?.id, { enabled: Boolean(seller) && isOwner });

  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const signOutMutation = useSignOutEverywhereMutation();
  const transferMutation = useTransferOwnershipMutation();
  const deleteMutation = useDeleteOrgMutation();
  const exportMutation = useOrgExportMutation();

  if (!seller) {
    return null;
  }

  if (!isOwner) {
    return <Navigate replace to="/app/settings" />;
  }

  const orgName = seller.name || '';
  const sessions = sessionsQuery.data?.sessions || [];
  // Transfer targets: active members who aren't the current owner.
  const transferTargets = (teamQuery.data?.members || teamQuery.data || [])
    .filter((member) => member.role !== 'owner' && member.status !== 'pending');

  const confirmSignOutEverywhere = () => {
    signOutMutation.mutate({}, {
      onSuccess: () => {
        // All tokens (incl. this one) are dead — force the local logout flow.
        auth.logout().finally(() => {
          router.invalidate().finally(() => navigate({ to: '/auth/login' }));
        });
      },
      onError: () => {
        setConfirmSignOut(false);
        push('Could not sign out other sessions', 'danger');
      },
    });
  };

  const submitTransfer = (payload) => {
    setTransferError(null);
    transferMutation.mutate({ sellerId: seller.id, payload, mineKey }, {
      onSuccess: () => {
        setTransferOpen(false);
        push('Ownership transferred — you are now a Manager');
      },
      onError: (error) => {
        setTransferError(firstApiError(error?.response?.data, 'confirm_name', 'membership_id')
          || 'Could not transfer ownership');
      },
    });
  };

  const openTransfer = () => {
    setTransferError(null);
    setTransferOpen(true);
  };

  const openDelete = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const submitDelete = (payload) => {
    setDeleteError(null);
    deleteMutation.mutate({ sellerId: seller.id, payload, mineKey }, {
      onSuccess: () => {
        setDeleteOpen(false);
        // Drop the deleted org and re-resolve membership. The /app shell routes
        // to onboarding when none remain; otherwise the first org takes over.
        router.invalidate().finally(() => navigate({ to: '/app/settings' }));
      },
      onError: (error) => {
        setDeleteError(firstApiError(error?.response?.data, 'confirm_name') || 'Could not delete organization');
      },
    });
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Security" subtitle="Manage sessions and the org danger zone." />

      <div className="flex w-full flex-col gap-3.5 px-4 pb-8 lg:px-0">
        <SessionsCard
          sessions={sessions}
          error={sessionsQuery.isError}
          loading={sessionsQuery.isPending}
        />

        <Button
          type="button"
          variant="secondary"
          disabled={signOutMutation.isPending}
          className="h-[42px] gap-2 border-danger/40 bg-surface-1 text-[13px] text-danger hover:bg-surface-1"
          onClick={() => setConfirmSignOut(true)}
        >
          <LogOut className="size-4" />
          Sign out everywhere
        </Button>

        <span className="mt-3 px-1 text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
          Danger zone
        </span>

        <Card title="Transfer ownership" subtitle="Hand the org to another member. You become a Manager.">
          <Button
            type="button"
            variant="secondary"
            className="h-[38px] gap-1.5 bg-surface-1 px-3.5 text-[12.5px]"
            onClick={openTransfer}
          >
            <UserCog className="size-[15px]" />
            Transfer
          </Button>
        </Card>

        <Card title="Export organization data" subtitle="Download a zip of your inventory, listings, and sales.">
          <Button
            type="button"
            variant="secondary"
            className="h-[38px] gap-1.5 bg-surface-1 px-3.5 text-[12.5px]"
            loading={exportMutation.isPending}
            onClick={() => exportMutation.mutate(
              { sellerId: seller.id, fileName: `${seller.name || 'organization'}-export.zip` },
              { onError: () => push('Could not export data', 'danger') },
            )}
          >
            {exportMutation.isPending ? null : <Download className="size-[15px]" />}
            {exportMutation.isPending ? 'Preparing…' : 'Export'}
          </Button>
        </Card>

        <Card danger title="Delete organization" subtitle="Permanently remove this org and everything in it.">
          <Button
            type="button"
            variant="secondary"
            className={cn(
              'h-[38px] gap-1.5 border-danger/50 bg-surface-1 px-3.5 text-[12.5px] text-danger hover:bg-surface-1',
            )}
            onClick={openDelete}
          >
            <Trash2 className="size-[15px]" />
            Delete
          </Button>
        </Card>
      </div>

      {confirmSignOut
        ? (
          <ConfirmDialog
            title="Sign out everywhere?"
            busy={signOutMutation.isPending}
            confirmLabel="Sign out"
            onConfirm={confirmSignOutEverywhere}
            onCancel={() => setConfirmSignOut(false)}
            body="This ends every active session, including this one. You'll need to sign in again."
          />
        )
        : null}

      {transferOpen
        ? (
          <TransferModal
            members={transferTargets}
            busy={transferMutation.isPending}
            orgName={orgName}
            error={transferError}
            onSubmit={submitTransfer}
            onClose={() => setTransferOpen(false)}
          />
        )
        : null}

      {deleteOpen
        ? (
          <DeleteOrgModal
            busy={deleteMutation.isPending}
            orgName={orgName}
            error={deleteError}
            onSubmit={submitDelete}
            onClose={() => setDeleteOpen(false)}
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

Security.displayName = 'Security';
export default Security;
